import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { RegistrationStammdatenPlugin } from '../plugin-types.js';

export const icsrmRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::icsrm',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new IcsrmStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(IcsrmStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => IcsrmStammdaten,
};

export enum IcsrmGenderEnum {
  ICSRM_MALE = 'ICSRM_MALE',
  ICSRM_FEMALE = 'ICSRM_FEMALE',
  ICSRM_DIVERSE = 'ICSRM_DIVERSE',
  ICSRM_NOT_SPECIFIED = 'ICSRM_NOT_SPECIFIED',
}
registerEnum({ enum: IcsrmGenderEnum, name: 'IcsrmGenderEnum' });

export enum IcsrmUniEnum {
  ICSRM_FRA = 'ICSRM_FRA',
  ICSRM_GU = 'ICSRM_GU',
  ICSRM_HDA = 'ICSRM_HDA',
  ICSRM_HSRM = 'ICSRM_HSRM',
  ICSRM_ICSRM = 'ICSRM_ICSRM',
  ICSRM_JGU = 'ICSRM_JGU',
  ICSRM_ANDERE = 'ICSRM_ANDERE',
}
registerEnum({ enum: IcsrmUniEnum, name: 'IcsrmUniEnum' });

// sorted alphabetically by german translation
export enum IcsrmDepartmentEnum {
  ICSRM_ARCHITECTURE = 'ICSRM_ARCHITECTURE',
  ICSRM_CIVIL_ENGINEERING = 'ICSRM_CIVIL_ENGINEERING',
  ICSRM_ELECTRICAL_ENGINEERING = 'ICSRM_ELECTRICAL_ENGINEERING',
  ICSRM_EVENT_MANAGEMENT = 'ICSRM_EVENT_MANAGEMENT',
  ICSRM_HEALTH = 'ICSRM_HEALTH',
  ICSRM_ENGINEERING_TECHNOLOGY = 'ICSRM_ENGINEERING_TECHNOLOGY',
  ICSRM_COMPUTER_SCIENCE = 'ICSRM_COMPUTER_SCIENCE',
  ICSRM_ARTS = 'ICSRM_ARTS',
  ICSRM_PEDAGOGY = 'ICSRM_PEDAGOGY',
  ICSRM_LINGUISTICS = 'ICSRM_LINGUISTICS',
  ICSRM_MECHANICAL_ENGINEERING = 'ICSRM_MECHANICAL_ENGINEERING',
  ICSRM_MARKETING = 'ICSRM_MARKETING',
  ICSRM_MATHEMATICS = 'ICSRM_MATHEMATICS',
  ICSRM_PSYCHOLOGY = 'ICSRM_PSYCHOLOGY',
  ICSRM_LAW = 'ICSRM_LAW',
  ICSRM_SOCIAL_SCIENCE = 'ICSRM_SOCIAL_SCIENCE',
  ICSRM_SPORTS = 'ICSRM_SPORTS',
  ICSRM_ARGICULTURE = 'ICSRM_ARGICULTURE',
  ICSRM_ECONOMICS_MANAGEMENT = 'ICSRM_ECONOMICS_MANAGEMENT',
  ICSRM_OTHER = 'ICSRM_OTHER',
}
registerEnum({ enum: IcsrmDepartmentEnum, name: 'IcsrmDepartmentEnum' });

export enum IcsrmDesiredDegreeEnum {
  ICSRM_BACHELOR = 'ICSRM_BACHELOR',
  ICSRM_MASTER = 'ICSRM_MASTER',
  ICSRM_STAATSEXAMEN_LEHRAMT = 'ICSRM_STAATSEXAMEN_LEHRAMT',
  ICSRM_STAATSEXAMEN_MEDIZIN_PHARMAZIE = 'ICSRM_STAATSEXAMEN_MEDIZIN_PHARMAZIE',
  ICSRM_STAATSEXAMEN_JURA = 'ICSRM_STAATSEXAMEN_JURA',
  ICSRM_PROMOTION = 'ICSRM_PROMOTION',
  ICSRM_SONSTIGE = 'ICSRM_SONSTIGE',
}
registerEnum({ enum: IcsrmDesiredDegreeEnum, name: 'IcsrmDesiredDegreeEnum' });

@Entity()
export class IcsrmStammdaten extends UserStammdaten {
  static override _class = 'IcsrmStammdaten';

  override getTranslationNamespace() {
    return 'icsrm';
  }

  // 1. Persönliche Angaben
  // Geschlecht: männlich/ weiblich/ divers/ ohne Angabe
  @validate(nonOptional())
  @Field(type => IcsrmGenderEnum)
  gender: Maybe<IcsrmGenderEnum>;

  @validate(nonOptional())
  @Field(type => DateTime)
  dateOfBirth: Maybe<DateTime>;

  // Aktueller Wohnort:
  @validate(({ value, target, key }) => {
    const errorFields = (
      !target.address
        ? [{ path: ['address'], target }]
        : [
            ...(!target.address.zipCity
              ? [{ path: ['address', 'zipCity'], target: target.address }]
              : [
                  !target.address?.zipCity?.zip
                    ? { path: ['address', 'zipCity', 'zip'], target: target.address?.zipCity }
                    : null,
                  !target.address?.zipCity?.city
                    ? { path: ['address', 'zipCity', 'city'], target: target.address?.zipCity }
                    : null,
                ]),
          ]
    ).filter(Boolean) as Array<{ path: string[]; target: any }>;

    if (errorFields.length)
      throw new MultipleValidationError(
        errorFields.map(
          ({ path, target }) =>
            new ValidationError(`Key ${path.slice(-1)[0]} is required!`, {
              key: path.slice(-1)[0],
              translationKey: 'validation_non_optional',
              path: path.slice(0, -1),
              target,
            }),
        ),
      );
  })
  @Field(type => Address, { default: v => v ?? new Address({}) })
  address: Maybe<Address>;

  // Staatsangehörigkeit: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  nationality: Maybe<string>;

  // Geburtsland: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  countryOfBirth: Maybe<string>;

  // Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren): ja / nein
  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: value => value ?? false })
  isImmigrant: Maybe<boolean>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: value => value ?? false })
  foreignUniQualification: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => String)
  phoneNumber: Maybe<string>;

  // 2. Studium
  @validate(nonOptional())
  @Field(type => IcsrmUniEnum)
  currentUni: Maybe<IcsrmUniEnum>;

  // Fachsemester:
  @validate(nonOptional())
  @Field(type => String)
  semester: Maybe<string>;

  // Studiengang:
  @validate(nonOptional())
  @Field(type => String)
  courseOfStudy: Maybe<string>;

  // Fachbereich: (Liste wie aktuell)
  @validate(nonOptional())
  @Field(type => IcsrmDepartmentEnum)
  department: Maybe<IcsrmDepartmentEnum>;

  // Angestrebter Abschluss:
  @validate(nonOptional())
  @Field(type => IcsrmDesiredDegreeEnum)
  desiredDegree: Maybe<IcsrmDesiredDegreeEnum>;
}
